import React, { useState } from 'react';
import { View, TouchableOpacity, StyleSheet } from 'react-native';
import { useTheme } from 'react-native-paper';
import Svg, { Path } from 'react-native-svg';
import QText from './QText';
import InvoiceComponent from './InvoiceComponent';

// Simple chevron icons for accordion
const ChevronDown = ({ size = 20, color = 'black' }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24">
    <Path
      d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"
      fill={color}
    />
  </Svg>
);

const ChevronUp = ({ size = 20, color = 'black' }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24">
    <Path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z" fill={color} />
  </Svg>
);

const CollapsibleInvoice = ({
  title,
  subtitle,
  isExpanded: initialExpanded = true,
  children,
  invoiceProps,
  showAmountInHeader = true,
  paymentAmount,
  paymentStatus,
  hasDiscounts = false,
  services = [],
  ...otherProps
}) => {
  const [isExpanded, setIsExpanded] = useState(initialExpanded);
  const theme = useTheme();
  const styles = createStyles(theme);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  // Get payment status color
  const getPaymentStatusColor = () => {
    const status = paymentStatus?.toLowerCase();
    if (status === 'success' || status === 'paid' || status === 'completed') {
      return '#22C55E'; // Green
    } else if (status === 'failed' || status === 'error') {
      return '#FF6B6B'; // Red
    } else if (status === 'processing') {
      return '#FF9800'; // Orange
    } else {
      return '#2196F3'; // Blue for pending
    }
  };

  return (
    <View style={styles.container}>
      {/* Collapsible Header */}
      <TouchableOpacity
        style={styles.header}
        onPress={toggleExpanded}
        activeOpacity={0.7}
      >
        <View style={styles.headerContent}>
          <View style={styles.headerLeft}>
            <QText style={styles.title}>{title}</QText>
            {subtitle && <QText style={styles.subtitle}>{subtitle}</QText>}
          </View>

          <View style={styles.headerRight}>
            {showAmountInHeader && paymentAmount && (
              <View style={styles.amountContainer}>
                {hasDiscounts ? (
                  <View style={styles.discountAmountContainer}>
                    {/* Calculate original total and discounted total from services */}
                    {(() => {
                      const originalTotal = services.reduce((sum, service) => {
                        const quantity = service.quantity || 1;
                        return sum + (service.price || 0) * quantity;
                      }, 0);

                      const discountedTotal = services.reduce(
                        (sum, service) => {
                          const quantity = service.quantity || 1;
                          const servicePrice =
                            service.discounted_price || service.price || 0;
                          return sum + servicePrice * quantity;
                        },
                        0
                      );

                      const discountPercentage =
                        originalTotal > 0
                          ? (
                              ((originalTotal - discountedTotal) /
                                originalTotal) *
                              100
                            ).toFixed(0)
                          : 0;

                      return (
                        <>
                          <QText style={styles.originalAmountText}>
                            ₹{originalTotal.toFixed(2)}
                          </QText>
                          <QText style={styles.discountedAmountText}>
                            ₹{discountedTotal.toFixed(2)}
                          </QText>
                          <QText style={styles.discountBadge}>
                            -{discountPercentage}%
                          </QText>
                        </>
                      );
                    })()}
                  </View>
                ) : (
                  <QText style={styles.amountText}>₹{paymentAmount}</QText>
                )}
                <QText
                  style={[
                    styles.statusText,
                    { color: getPaymentStatusColor() },
                  ]}
                >
                  {paymentStatus || 'Pending'}
                </QText>
              </View>
            )}

            <View style={styles.arrowContainer}>
              {isExpanded ? (
                <ChevronUp size={20} color={theme.colors.secondaryText} />
              ) : (
                <ChevronDown size={20} color={theme.colors.secondaryText} />
              )}
            </View>
          </View>
        </View>
      </TouchableOpacity>

      {/* Collapsible Content */}
      {isExpanded && (
        <View style={styles.content}>
          {children || <InvoiceComponent {...invoiceProps} />}
        </View>
      )}
    </View>
  );
};

const createStyles = (theme) =>
  StyleSheet.create({
    container: {
      marginBottom: 16,
      backgroundColor: theme.colors.onBackground,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: theme.colors.OnBackgroundDarkBorder,
      overflow: 'hidden',
    },
    header: {
      backgroundColor: theme.colors.background,
      paddingHorizontal: 16,
      paddingVertical: 12,
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.OnBackgroundDarkBorder,
    },
    headerContent: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    headerLeft: {
      flex: 1,
    },
    headerRight: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
    },
    title: {
      fontSize: 16,
      fontWeight: 'bold',
      color: theme.colors.secondaryText,
      marginBottom: 2,
    },
    subtitle: {
      fontSize: 12,
      color: theme.colors.placeHolderText,
    },
    amountContainer: {
      alignItems: 'flex-end',
    },
    amountText: {
      fontSize: 14,
      fontWeight: 'bold',
      color: theme.colors.secondaryText,
    },
    discountAmountContainer: {
      alignItems: 'flex-end',
    },
    originalAmountText: {
      fontSize: 12,
      color: theme.colors.placeHolderText,
      textDecorationLine: 'line-through',
      marginBottom: 2,
    },
    discountedAmountText: {
      fontSize: 14,
      fontWeight: 'bold',
      color: theme.colors.primary,
    },
    discountBadge: {
      fontSize: 10,
      color: theme.colors.success,
      fontWeight: '600',
      backgroundColor: theme.colors.success + '15',
      paddingHorizontal: 4,
      paddingVertical: 1,
      borderRadius: 4,
      marginTop: 2,
    },
    statusText: {
      fontSize: 10,
      fontWeight: '500',
      textTransform: 'uppercase',
    },
    arrowContainer: {
      padding: 4,
    },
    content: {
      backgroundColor: theme.colors.onBackground,
    },
  });

export default CollapsibleInvoice;
